import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { 
  Sparkles, 
  BookOpen, 
  Brush, 
  MessageCircle, 
  User, 
  Crown,
  PenTool,
  ArrowRight,
  Share,
  AlignJustify, 
  Layers,
  Bot,
  FileQuestion
} from "lucide-react";

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  isPremium?: boolean;
  isBasic?: boolean;
}

export const CreativeSuite = () => {
  const [activeTab, setActiveTab] = useState<string>("stories");
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Updated tools based on actual feature limits from database
  const creativeTools: Record<string, Tool[]> = {
    stories: [
      {
        id: "ai-writing",
        title: t('landing.creativeSuite.tools.aiWriting.title'),
        description: t('landing.creativeSuite.tools.aiWriting.description'),
        icon: <PenTool className="w-6 h-6 text-purple-400" />,
        isBasic: false,
      },
      {
        id: "genres-themes",
        title: t('landing.creativeSuite.tools.genresThemes.title'),
        description: t('landing.creativeSuite.tools.genresThemes.description'),
        icon: <Layers className="w-6 h-6 text-green-400" />,
        isBasic: true,
      },
      {
        id: "sequels",
        title: t('landing.creativeSuite.tools.sequels.title'),
        description: t('landing.creativeSuite.tools.sequels.description'),
        icon: <BookOpen className="w-6 h-6 text-orange-400" />,
        isPremium: true,
      }
    ],
    characters: [
      {
        id: "character-builder",
        title: t('landing.creativeSuite.tools.characterBuilder.title'),
        description: t('landing.creativeSuite.tools.characterBuilder.description'),
        icon: <User className="w-6 h-6 text-yellow-400" />,
        isBasic: true,
      },
      {
        id: "ai-portraits",
        title: t('landing.creativeSuite.tools.aiPortraits.title'),
        description: t('landing.creativeSuite.tools.aiPortraits.description'),
        icon: <Brush className="w-6 h-6 text-pink-400" />,
        isPremium: true,
      },
      {
        id: "character-extract",
        title: t('landing.creativeSuite.tools.characterExtract.title'),
        description: t('landing.creativeSuite.tools.characterExtract.description'),
        icon: <FileQuestion className="w-6 h-6 text-teal-400" />,
        isPremium: true,
      }
    ],
    premium: [
        {
            id: "sharing",
            title: t('landing.creativeSuite.tools.sharing.title'),
            description: t('landing.creativeSuite.tools.sharing.description'),
            icon: <Share className="w-6 h-6 text-blue-400" />,
            isBasic: true,
        },
      {
        id: "unlimited-storage",
        title: t('landing.creativeSuite.tools.unlimitedStorage.title'),
        description: t('landing.creativeSuite.tools.unlimitedStorage.description'),
        icon: <BookOpen className="w-6 h-6 text-purple-400" />,
        isBasic: true,
        isPremium: true,
      },
      {
        id: "stats-badges",
        title: t('landing.creativeSuite.tools.statsBadges.title'),
        description: t('landing.creativeSuite.tools.statsBadges.description'),
        icon: <Sparkles className="w-6 h-6 text-green-400" />,
        isBasic: true,
        isPremium: true,
      }
    ]
  };

  const handleGetStarted = () => {
    navigate('/login'); // Redirect to login page
  };

  return (
    <section className="py-20 relative overflow-hidden" id="creative-suite">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#1c1626] to-[#2d2443]" />
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-10" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">{t('landing.creativeSuite.title')}</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('landing.creativeSuite.description')}
          </p>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1 rounded-lg bg-gray-800/50 backdrop-blur-sm">
            {Object.keys(creativeTools).map((category) => (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={`px-5 py-2 rounded-md transition-all ${
                  activeTab === category
                    ? "bg-purple-600 text-white" 
                    : "text-gray-300 hover:text-white"
                }`}
              >
                {category === "premium" && <Crown className="inline-block w-4 h-4 mr-1" />}
                {t(`landing.creativeSuite.categories.${category}`)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {creativeTools[activeTab].map((tool) => (
            <div 
              key={tool.id}
              className="bg-gray-900/60 backdrop-blur-md rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-all shadow-lg hover:shadow-purple-900/20"
            >
              <div className="flex items-start mb-4">
                <div className="mr-4 p-3 rounded-lg bg-gray-800/70">
                  {tool.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center">
                    {tool.title}
                    {tool.isPremium && (
                      <div title={t('landing.creativeSuite.premiumFeature')}>
                        <Crown className="ml-2 w-4 h-4 text-yellow-400" />
                      </div>
                    )}
                  </h3>
                </div>
              </div>
              
              <p className="text-gray-300 mb-4">
                {tool.description}
              </p>
              
              {tool.isPremium ? (
                <div className="text-yellow-400 text-sm font-medium flex items-center">
                  <Crown className="w-4 h-4 mr-1" />
                  {t('landing.creativeSuite.premiumFeature')}
                </div>
              ) : tool.isBasic ? (
                <div className="text-blue-400 text-sm font-medium">
                  {t('landing.creativeSuite.basicFeature')}
                </div>
              ) : (
                <div className="text-green-400 text-sm font-medium">
                  {t('landing.creativeSuite.freeFeature')}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className="text-center">
          <div className="inline-block p-[2px] rounded-lg bg-gradient-to-r from-purple-600 to-blue-500">
            <Button 
              className="text-lg bg-gray-900 hover:bg-gray-800 px-8 py-6 h-auto"
              onClick={handleGetStarted}
            >
              {t('landing.creativeSuite.getStarted')} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="text-gray-400 mt-4">
            {t('landing.creativeSuite.noCreditCard')}
          </p>
        </div>
      </div>
    </section>
  );
};
